import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

/**
 * Возвращает «полный» маршрут текущей страницы — `pathname + hash` — и обновляется
 * при ЛЮБОЙ смене адреса, включая переключение вкладок карточки документа.
 *
 * `pathname` берётся из react-router (`useLocation`), а `hash` — напрямую из
 * `window.location`. Так сделано потому, что вкладки карточки (`molecules/Tabs`)
 * меняют хэш через `window.history.pushState`, минуя react-router: `pushState`
 * не порождает ни `popstate`, ни `hashchange`, поэтому `useLocation().hash`
 * остаётся устаревшим. Чтобы туры могли таргетиться на конкретную вкладку
 * (например, «Выбор победителя» → `#page-tlcCustomWinnerChoice`), хэш читаем из
 * `window.location` и подхватываем «тихий» `pushState` коротким поллингом.
 *
 * Сигнал минимально-инвазивен: состояние обновляется только при реальной смене
 * хэша, поэтому потребители ре-рендерятся лишь при навигации.
 */
export function useCurrentRoute(): string {
    const location = useLocation();
    const [hash, setHash] = useState<string>(() => window.location.hash);

    useEffect(() => {
        const sync = () => {
            const next = window.location.hash;
            setHash((prev) => (prev === next ? prev : next));
        };
        // react-router-навигация может сменить и хэш — синхронизируемся сразу.
        sync();
        window.addEventListener('popstate', sync);
        window.addEventListener('hashchange', sync);
        // pushState из Tabs не шлёт событий — ловим коротким поллингом.
        const intervalId = setInterval(sync, 400);
        return () => {
            window.removeEventListener('popstate', sync);
            window.removeEventListener('hashchange', sync);
            clearInterval(intervalId);
        };
    }, [location.pathname, location.search, location.hash]);

    return location.pathname + hash;
}
